import { HeaderV3 } from "@circle-react-shared/uikit/HeaderV3";
import { HeaderV4 } from "@circle-react-shared/uikit/HeaderV4";
import { CircleAIAppShell } from "@circle-react-uikit/CircleAIAppShell";
import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { useBackendProps } from "@circle-react/providers/AppContext";
import { useMatchRoute } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { t } from "@/i18n-js/instance";
import { WorkspaceBackground } from "@/react/components/Layout/WorkspaceBackground";
import { useIsAdminArea } from "@/react/components/SettingsApp/CompassCompat/useIsAdminArea";
import { SettingsShell } from "@/react/components/SettingsApp/SettingsShell";
import { SideBar } from "@/react/components/Spaces/Sidebar";
import { GoLivePoweredBy } from "@/react/components/Spaces/SidebarV2/GoLivePoweredBy";
import { LiveIndicator } from "@/react/components/Spaces/SidebarV2/SidebarFooter";
import { FooterSection as SidebarV3FooterSection } from "@/react/components/Spaces/SidebarV3/FooterSection";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { AppBar } from "../AppBar";
import { useSidebarDisabledV3 } from "../DynamicLayout/useSidebarDisabledV3";
import { PageHeaderContainer, PageRailbarContainer } from "../WorkspacePage";
import { CanvasInset } from "./CanvasInset";
import { useIsPageChromeHidden } from "./ShellBypassContext";
import { ShellRailContent } from "./ShellRailContent";
import { ShellRailPanel } from "./ShellRailPanel";

interface ShellNativeContentProps {
  children: ReactNode;
}

export const ShellNativeContent = ({ children }: ShellNativeContentProps) => {
  const { isCircleV4 } = useCircleV4();
  const isAdminArea = useIsAdminArea();
  const isSidebarDisabled = useSidebarDisabledV3();
  const isPageChromeHidden = useIsPageChromeHidden();
  const { display_header: shouldDisplayHeader = true } = useBackendProps();
  const setLeftSidebarVisible = useCircleAIShellStore(
    s => s.setLeftSidebarVisible,
  );

  const matchRoute = useMatchRoute();
  const isUnifiedInboxRoute = Boolean(
    matchRoute({ to: "/settings/inbox", fuzzy: true }),
  );

  useEffect(() => {
    setLeftSidebarVisible(isUnifiedInboxRoute || !isSidebarDisabled);
  }, [isSidebarDisabled, setLeftSidebarVisible, isUnifiedInboxRoute]);

  // Admin/Settings area renders its own chrome (SettingsTopBar + workspace, no
  // member spaces sidebar) through the same single-Inset canvas path as the
  // member area — no `useShellBypass`/`CanvasOnlyContent` double-wrap.
  if (isAdminArea) {
    return <SettingsShell>{children}</SettingsShell>;
  }

  const shouldRenderCommunityHeader =
    shouldDisplayHeader && !isPageChromeHidden;

  return (
    <CanvasInset alwaysShowHeader={isPageChromeHidden}>
      {shouldRenderCommunityHeader && (
        <CircleAIAppShell.Layout.Inset.Header>
          {isCircleV4 ? <HeaderV4 /> : <HeaderV3 />}
        </CircleAIAppShell.Layout.Inset.Header>
      )}
      <CircleAIAppShell.Layout.Inset.Body>
        {!isSidebarDisabled && (
          <>
            <CircleAIAppShell.Layout.Sidebar
              aria-label={t("circle_ai_shell.sidebar")}
            >
              <CircleAIAppShell.Layout.Sidebar.Body>
                <SideBar />
              </CircleAIAppShell.Layout.Sidebar.Body>
              <CircleAIAppShell.Layout.Sidebar.Footer>
                {isCircleV4 ? (
                  <SidebarV3FooterSection />
                ) : (
                  <>
                    <LiveIndicator />
                    <GoLivePoweredBy />
                  </>
                )}
              </CircleAIAppShell.Layout.Sidebar.Footer>
            </CircleAIAppShell.Layout.Sidebar>
            <CircleAIAppShell.SidebarResizeHandle />
          </>
        )}
        <CircleAIAppShell.Layout.Workspace>
          {!isPageChromeHidden && (
            <CircleAIAppShell.Layout.Workspace.Header>
              <AppBar />
              <PageHeaderContainer />
            </CircleAIAppShell.Layout.Workspace.Header>
          )}
          <CircleAIAppShell.Layout.Workspace.Body>
            <WorkspaceBackground>{children}</WorkspaceBackground>
          </CircleAIAppShell.Layout.Workspace.Body>
        </CircleAIAppShell.Layout.Workspace>
        <CircleAIAppShell.PanelResizeHandle />
        <ShellRailPanel>
          <ShellRailContent>
            <PageRailbarContainer />
          </ShellRailContent>
        </ShellRailPanel>
      </CircleAIAppShell.Layout.Inset.Body>
    </CanvasInset>
  );
};
